import {useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import api from '../../api/axios';
import {useAuth} from '../../contexts/AuthContext';
import {MailIcon, PhoneIcon} from '../../components/Icons';

export default function VerifyInterrupted() {
    const location = useLocation();
    const navigate = useNavigate();
    const {
        email = '',
        phone = '',
        requiresEmail = true,
        requiresPhone = true,
        socialParams = null,
        tempAccessToken = null
    } = location.state || {};
    const auth = useAuth();

    const [emailOtp, setEmailOtp] = useState('');
    const [phoneOtp, setPhoneOtp] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSubmitting(true);
        try {
            const payload = {
                email: requiresEmail ? email : undefined,
                phone: requiresPhone ? phone : undefined,
                emailOtp: emailOtp || undefined,
                phoneOtp: phoneOtp || undefined,
                socialParams
            };
            if (tempAccessToken) payload.tempAccessToken = tempAccessToken;
            // TODO: This endpoint does not exist in api.php — route was migrated
            const res = await api.post('/api/v1/portal/verify', payload);
            const data = res?.data || {};
            // If verification completes and backend establishes a session (or returns an accessToken), apply it and go to dashboard
            if (data.success && data.accessToken) {
                try {
                    if (auth && typeof auth.setAccessToken === 'function') auth.setAccessToken(data.accessToken);
                } catch {
                    /* ignore */
                }
                navigate('/dashboard');
                return;
            }
            // Otherwise, if verification succeeded but requires another step, route to SSO sign-in popup
            if (data.success) {
                navigate('/auth/popup/sso');
            }
        } catch (err) {
            setError(err?.response?.data?.message || 'Verification failed. Please try again.');
            console.error('Verification error', err);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="max-w-md w-full bg-white dark:bg-brand-card p-6 rounded-xl border border-slate-200 dark:border-slate-800 shadow-2xl">
            <header className="mb-4">
                <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">Verification Required</h1>
                <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">Enter the one-time codes sent to your
                    contact methods to continue.</p>
            </header>

            <p className="text-sm text-slate-700 dark:text-slate-300 mb-4">We sent OTPs to the following addresses.
                Enter the codes below. Mobile devices may autofill the codes.</p>

            <form onSubmit={handleSubmit} className="space-y-4">
                {requiresEmail && (
                    <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/60">
                        <div className="flex items-center gap-2 mb-2">
                            <div className="p-1 rounded bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400">
                                <MailIcon className="w-3.5 h-3.5" />
                            </div>
                            <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                                Email Verification: <span className="font-mono text-slate-500 dark:text-slate-400">{email}</span>
                            </label>
                        </div>
                        <input
                            value={emailOtp}
                            onChange={(e) => setEmailOtp(e.target.value)}
                            placeholder="123456"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            className="w-full text-lg tracking-widest text-center bg-white dark:bg-gray-800 border border-slate-300 dark:border-slate-700 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                        />
                    </div>
                )}

                {requiresPhone && (
                    <div className="p-3.5 rounded-xl bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/60">
                        <div className="flex items-center gap-2 mb-2">
                            <div className="p-1 rounded bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400">
                                <PhoneIcon className="w-3.5 h-3.5" />
                            </div>
                            <label className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                                Phone (SMS) Verification: <span className="font-mono text-slate-500 dark:text-slate-400">{phone}</span>
                            </label>
                        </div>
                        <input
                            value={phoneOtp}
                            onChange={(e) => setPhoneOtp(e.target.value)}
                            placeholder="123456"
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            className="w-full text-lg tracking-widest text-center bg-white dark:bg-gray-800 border border-slate-300 dark:border-slate-700 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                        />
                    </div>
                )}

                {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

                <div className="flex items-center justify-between gap-3">
                    <button type="submit" disabled={submitting}
                            className="flex-1 bg-indigo-600 hover:bg-indigo-500 text-white rounded py-2 px-4">{submitting ? 'Verifying…' : 'Verify codes'}</button>
                    <button type="button" onClick={() => console.log('Resend code requested')}
                            className="text-sm text-slate-600 dark:text-slate-300 underline">Resend
                    </button>
                </div>

                <p className="text-xs text-slate-500 dark:text-slate-400">If you didn't request this, please contact
                    your administrator.</p>
            </form>
        </div>
    );
}

